import DataTable, {type DataTableRef} from 'datatables.net-react'
import 'datatables.net-dt/css/dataTables.dataTables.css';
import DT, {type Config} from 'datatables.net';
import React, {forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import type {Keypair} from "../../lib/ModelTypes.ts";
import {keypair, KeypairAPIConnectorError} from "../../lib/Keypair.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import {datatable_language_pack} from "./DatatableLanguagePack.tsx";
import {useTranslation} from "react-i18next";

export interface KeyDataRef {
    getSelectedData: () => Keypair[];
    redraw: () => void;
    onClickDelete: (name: string) => void;
}

type DataTableProps = {
    ref?: Ref<KeyDataRef>;
};

const KeyPairTable = forwardRef<KeyDataRef, DataTableProps>((_props, ref) => {
    const {t} = useTranslation();

    DataTable.use(DT);
    const dataTableRef = useRef<DataTableRef>(null);
    const [data, setData] = useState<{
        keypair: Keypair[];
        checked: { [key: string]: boolean };
        dataLoading: boolean;
    }>({
        keypair: [],
        checked: {},
        dataLoading: true,
    });

    useImperativeHandle(ref, () => ({
        getSelectedData: () =>
            data.keypair.filter((row) => data.checked[row.name]),
        redraw: () => redraw().finally(),
        onClickDelete: (name: string) => onClickDelete(name),
    }));


    function handleSelectAll(e: React.UIEvent) {
        const otherCheckboxes = document.getElementsByClassName("check-keypair");
        Array.from(otherCheckboxes).forEach((element) => {
            const checkbox = element as HTMLInputElement;
            checkbox.checked = (e.target as HTMLInputElement).checked;
        });
        data.keypair.forEach((item) => {
            handleCheckboxChange(item.name, (e.target as HTMLInputElement).checked)
        })
    }

    const redraw = useCallback(async () => {
        try {
            const res = await keypair.get();
            setData(prev => ({...prev, keypair: res!.data}));
            setData(prev => ({...prev, dataLoading: false}));
        } catch (err) {
            await swal_util.show_error(`${t("keypair.errorKeyListLoad")} ${t("common.adminContact")}`);
            console.error(err);
        }
    }, [t]);

    const onClickDelete = useCallback(async (name: string) => {
        const result = await swal_util.show_question(
            t("keypair.deleteKeyPair"),
            t("keypair.deleteConfirm")
        );
        if (!result.isConfirmed) return;

        try {
            swal_util.show_loading(t("keypair.deleting"));
            await keypair.delete(name);
            await redraw();

            await swal_util.hide_loading();
            await swal_util.show_success(t("keypair.deleted"));
        } catch (err) {
            await swal_util.hide_loading();

            if (err instanceof KeypairAPIConnectorError) {
                const text = err.localization.replace("{name}", name);
                await swal_util.show_warning(text);
            } else {
                await swal_util.show_error(
                    `${t("keypair.errorKeyDelete")} ${t("common.adminContact")}`
                );
            }
            console.error(err);
        }
    }, [redraw, t]);

    const onClickSetDefault = useCallback(async (name: string) => {
        try {
            await keypair.setDefault(name);
            await redraw();
        } catch (err) {
            if (err instanceof KeypairAPIConnectorError) {
                await swal_util.show_error(`${err.localization}`);
            } else {
                await swal_util.show_error(`${t("keypair.errorKeyConfig")} ${t("common.adminContact")}`);
            }
            console.error(err);
        }
    }, [redraw, t]);


    function handleCheckboxChange(name: string, checked: boolean) {
        setData((prevData) => ({
            ...prevData,
            checked: {
                ...prevData.checked,
                [name]: checked,
            },
        }));
    }

    const columns = [
        {
            data: 'name',
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            render: (data: string) => {
                return `<input type="checkbox" class="check-keypair form-check-input" data-id="${data}" value="${data}" >`;
            },
        },
        {data: 'name'},
        {data: 'finger_print'},
        {
            data: 'name',
            render: (_data: string, _type: string, row: Keypair) => {
                const msg = row.default ? t("common.default") : t("common.setDefault");
                const deleteText = t("common.delete");

                const btn_default = `<button class="btn ${row.default ? "btn-primary" : "btn-outline-primary default"}" style="font-size: 14px">${msg}</button>`;
                const btn_delete = `<button class="btn btn-danger delete" data-id="${row.name}" style="font-size: 14px">${deleteText}</button>`;
                return `${btn_default} ${btn_delete}`
            },
            orderable: false,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ];
    const options: Config = {
        destroy: true,
        language: datatable_language_pack,
        paging: false,
        searching: false,
        order: [[1, "asc"]],
        ordering: true,
        lengthChange: false,
        info: false,
    }

    useEffect(() => {
        // 화면이 다시 그려질때 dom을 바인딩하여야 react가 정상 동작
        // option 의 createdRow를 사용하면 안됨

        const tableNodeApi = dataTableRef.current?.dt();
        const rowsApi = tableNodeApi?.rows;
        if (!rowsApi) return;
        const count = rowsApi().count();

        const buttonCallbackSets: {
            deleteButton: HTMLButtonElement | null;
            deleteCallback: () => void;
            defaultButton: HTMLButtonElement | null;
            defaultCallback: () => void;
        }[] = [];
        for (let i = 0; i < count; i++) {
            const headCell = tableNodeApi.cell(i, 0).node();

            const checkbox = headCell.querySelector("input[type=checkbox]") as HTMLInputElement;

            const actionCell = tableNodeApi.cell(i, 3).node();
            const deleteButton = actionCell.querySelector('.delete');
            const defaultButton = actionCell.querySelector('.default');

            const name = checkbox.value;

            checkbox.onchange = () => {
                handleCheckboxChange(checkbox.value, checkbox.checked);
            }
            const deleteCallback = () => onClickDelete(name);
            const defaultCallback = () => onClickSetDefault(name);

            const buttonsSet = {
                deleteButton,
                deleteCallback,
                defaultButton,
                defaultCallback,
            } as typeof buttonCallbackSets[number];
            deleteButton?.addEventListener?.('click', deleteCallback);
            defaultButton?.addEventListener?.('click', defaultCallback);

            buttonCallbackSets.push(buttonsSet);
        }

        return () => {
            buttonCallbackSets.forEach(({deleteButton, deleteCallback, defaultButton, defaultCallback}) => {
                deleteButton?.removeEventListener("click", deleteCallback);
                defaultButton?.removeEventListener("click", defaultCallback);
            });
        }
    }, [onClickDelete, onClickSetDefault, data]);

    return (
        <div style={{fontSize: "14px", position: "relative"}}>
            <DataTable ref={dataTableRef} data={data.keypair} columns={columns} className="display" options={options}>
                <thead>
                <tr>
                    <th className="text-center"><input className="form-check-input" type={"checkbox"}
                                                       onClick={(e) => handleSelectAll(e)}/></th>
                    <th>{t("common.name")}</th>
                    <th>{t("keypair.fingerprint")}</th>
                    <th>{t("common.action")}</th>
                </tr>
                </thead>
                <tfoot>
                <tr>
                    <td colSpan={columns.length} className="text-start text-secondary border-bottom">
                        <span>{data.dataLoading ? t("common.loadingList") : `${data.keypair.length} ${t("keypair.keyCount")}`}</span>
                    </td>
                </tr>
                </tfoot>
            </DataTable>
        </div>
    );
});

export default KeyPairTable;
